/**
 * Custom hook to get authentication token for admin API requests
 *
 * Returns Clerk session token for authenticated requests.
 * `adminFetch` is the preferred helper for finanzas/admin APIs (adds Bearer automatically).
 */

import { useAuth } from "@clerk/clerk-react";
import { useCallback } from "react";

export function useAdminAuth() {
  const { getToken, isLoaded, isSignedIn } = useAuth();

  // Stable reference — `getToken` from Clerk is stable across renders,
  // so this callback won't change and won't trigger useEffect loops.
  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    try {
      const token = await getToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    } catch (error) {
      console.error("[useAdminAuth] Error getting Clerk token:", error);
    }

    return headers;
  }, [getToken]);

  /** Fetch with Clerk Bearer — used by finanzas and other admin hooks. */
  const adminFetch = useCallback(
    async (input: string, init?: RequestInit & { headers?: HeadersInit }) => {
      const authHeaders = await getAuthHeaders();
      const merged = new Headers(init?.headers);
      for (const [k, v] of Object.entries(authHeaders)) {
        if (!merged.has(k)) merged.set(k, v);
      }
      return fetch(input, { ...init, headers: merged });
    },
    [getAuthHeaders],
  );

  return { getAuthHeaders, adminFetch, isLoaded, isSignedIn };
}
